import { ResolverTag } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import { StarsLink } from '@ps/web-shared-ui';
import { minLength, numbersOnly, required } from '@ps/web-shared-validators';
import React from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import { Country as CountryEnum } from '@swf/core/enums';
import useOpenWindow from '@swf/core/hooks/useOpenWindow';
import { select } from '@swf/core/store';

import CivilNumber from '../../../../../../../../assets/civil-number.svg';
import FiscalImage from '../../../../../../../../assets/fiscal-code.svg';
import { TaxCodeType } from '../../../../../../../../validators/taxCode';
import { uniqueDocNumber } from '../../../../../../../../validators/uniqueDocNumber';
import InfoPrompt from '../../../../../../../InfoPrompt/InfoPrompt';
import SelectCitizenshipFlag from '../../../../../RMFCitizenship/components/SelectCitizenshipFlag/SelectCitizenshipFlag';
import DocumentNumberField from '../../../../../RMFDocumentation/components/DocumentNumberField/DocumentNumberField';
import DocumentTypeField from '../../../../../RMFDocumentation/components/DocumentTypeField/DocumentTypeField';
import TaxCodeField from '../TaxCodeField/TaxCodeField';

import {
  TXTWCL_Civil_ID_Number_format_incorre_ELL,
  TXTWCL_Document_number,
  TXTWCL_Fiscal_Code_XNIFX,
  TXTWCL_National_Civil_ID_Number,
  TXTWCL_Please_enter_your_8_digit_Nati_ELL,
  TXTWCL_Please_enter_your_9_digit_Fisc_ELL,
  TXTWCL_Please_enter_your_National_Civ_ELL,
  TXTWCL_Please_enter_your_identificati_ELL_1,
  TXTWCL_Please_log_in_to_an_existing_S_ELL,
  TXTWCL_The_National_Civil_ID_you_have_ELL,
  TXTWCL_You_may_only_own_a_single_Star_ELL
} from './PTFields.messages.json';
import { fieldWrapper, promptImage } from './PTFields.scss';
import Occupation from './components/Occupation/Occupation';

const DOC_NUMBER_MAX_LENGTH = 33;
const PT_DOC_NUMBER_LENGTH = 8;

const PTFields: React.FC = () => {
  const signupCountry = useSelector(select.account.getCountry);
  const options = useSelector(select.cms.getRequestOptions);

  const openWindowContactSupport = useOpenWindow({
    resolverTag: ResolverTag.ContactSupport,
    centered: true
  });

  const {
    input: { value: country }
  } = useField('country', { initialValue: signupCountry });

  const {
    input: { value: citizenship }
  } = useField('citizenship', { subscription: { value: true }, initialValue: signupCountry });

  const isCountryPortugal = country === CountryEnum.Portugal;
  const isPortugalCitizen = citizenship === CountryEnum.Portugal;

  return (
    <fieldset data-testid="identity-verification-form-fields">
      <SelectCitizenshipFlag />
      {!isPortugalCitizen && <DocumentTypeField country={country} citizenship={citizenship} />}
      {isCountryPortugal &&
        (isPortugalCitizen ? (
          <DocumentNumberField
            label={TXTWCL_National_Civil_ID_Number}
            validator={[
              required(TXTWCL_Please_enter_your_National_Civ_ELL),
              minLength(PT_DOC_NUMBER_LENGTH, {
                ...TXTWCL_Civil_ID_Number_format_incorre_ELL,
                values: { 0: PT_DOC_NUMBER_LENGTH }
              }),
              numbersOnly(TXTWCL_Civil_ID_Number_format_incorre_ELL),
              uniqueDocNumber(options, [
                TXTWCL_The_National_Civil_ID_you_have_ELL,
                TXTWCL_You_may_only_own_a_single_Star_ELL,
                {
                  ...TXTWCL_Please_log_in_to_an_existing_S_ELL,
                  values: {
                    a: (msg: React.ReactNode) => (
                      <StarsLink onClick={openWindowContactSupport}>{msg}</StarsLink>
                    )
                  }
                }
              ])
            ]}
            className={fieldWrapper}
            maxLength={PT_DOC_NUMBER_LENGTH}
            labelContent={
              <InfoPrompt
                title={{ translation: TXTWCL_National_Civil_ID_Number }}
                variant="deposit"
              >
                <Translation tagName="p" {...TXTWCL_Please_enter_your_8_digit_Nati_ELL} />
                <CivilNumber className={promptImage} />
              </InfoPrompt>
            }
          />
        ) : (
          <DocumentNumberField
            label={TXTWCL_Document_number}
            maxLength={DOC_NUMBER_MAX_LENGTH}
            validator={[required(TXTWCL_Please_enter_your_identificati_ELL_1)]}
          />
        ))}
      {
        <TaxCodeField
          className={fieldWrapper}
          taxCodeType={TaxCodeType.NIF}
          labelContent={
            <InfoPrompt title={{ translation: TXTWCL_Fiscal_Code_XNIFX }} variant="deposit">
              <Translation tagName="p" {...TXTWCL_Please_enter_your_9_digit_Fisc_ELL} />
              <FiscalImage className={promptImage} />
            </InfoPrompt>
          }
        />
      }
      <Occupation />
    </fieldset>
  );
};

export default PTFields;
